import React, { useEffect, useMemo, useState } from "react";
import { Youtube, CheckCircle, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n/I18nContext";
import api, { API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const FILTERS = ["all", "pending", "published"];

export default function YoutubeTab() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => api.get("/admin/youtube-queue").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const markPublished = async (id) => {
    await api.post(`/admin/youtube-queue/${id}/mark-published`);
    load();
  };

  const fileUrl = (path, forceDownload = false) => {
    const token = localStorage.getItem("ams_token");
    return `${API}/files/${path}?auth=${token}${forceDownload ? "&download=1" : ""}`;
  };

  const filteredItems = useMemo(() => {
    if (filter === "pending") return items.filter((o) => !o.youtube_published);
    if (filter === "published") return items.filter((o) => o.youtube_published);
    return items;
  }, [items, filter]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            className="rounded-full"
            data-testid={`admin-youtube-filter-${f}`}
            onClick={() => setFilter(f)}
          >
            {t(`admin.youtubeFilter${f.charAt(0).toUpperCase()}${f.slice(1)}`)}
          </Button>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">{t("admin.noResults")}</div>
      )}

      {filteredItems.map((o) => (
        <div key={o.order_id} className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center" data-testid={ADMIN.orderRow(o.order_id)}>
          {(o.generated?.thumbnail_storage_path || o.generated?.gallery_thumbnail_storage_path) && (
            <img
              src={fileUrl(o.generated.thumbnail_storage_path || o.generated.gallery_thumbnail_storage_path)}
              alt=""
              className="h-20 w-20 shrink-0 rounded-xl object-cover"
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">{o.generated?.title}</p>
              <Badge variant={o.youtube_published ? "secondary" : "default"} className="rounded-full text-[10px]">
                {o.youtube_published ? t("admin.youtubeStatusPublished") : t("admin.youtubeStatusPending")}
              </Badge>
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              {t("admin.youtubeCustomer")}: <span className="font-medium text-foreground">{o.customer_name || "-"}</span> ({o.customer_email || "?"})
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {t("admin.youtubeOrderId")}: {o.order_id} · {t("admin.youtubeOrderDate")} {o.completed_at ? new Date(o.completed_at).toLocaleDateString() : "-"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{o.generated?.youtube_description}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {(o.generated?.youtube_tags || []).map((tag) => (
                <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">#{tag}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(o.generated?.thumbnail_storage_path || o.generated?.gallery_thumbnail_storage_path) && (
              <a
                href={fileUrl(o.generated.thumbnail_storage_path || o.generated.gallery_thumbnail_storage_path, true)}
                download
                data-testid={ADMIN.downloadThumbnailBtn(o.order_id)}
              >
                <Button size="sm" variant="outline" className="rounded-full">{t("admin.downloadThumbnail")}</Button>
              </a>
            )}
            {o.generated?.versions?.[0] && (
              <a href={fileUrl(o.generated.versions[0].storage_path, true)} download>
                <Button size="sm" variant="outline" className="rounded-full">MP3</Button>
              </a>
            )}
            {o.youtube_published ? (
              <span className="flex items-center gap-1 text-xs text-success"><CheckCircle className="h-4 w-4" />{t("admin.youtubeStatusPublished")}</span>
            ) : (
              <Button size="sm" className="rounded-full" data-testid={ADMIN.markPublishedBtn(o.order_id)} onClick={() => markPublished(o.order_id)}>
                <Youtube className="mr-1 h-3.5 w-3.5" />
                {t("admin.markPublished")}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
